<div class="enrichment-theme enrichment-shell">
  <!-- Hero — branded header (status, readiness donut, language cards, primary
       actions). Scrolls away with the page. See
       docs/redesign/enrichment-edit-package/phase-1. -->
  <app-enrichment-edit-package-hero
    [entry]="active.entry()"
    [entries]="active.entries()"
    [pkg]="packageContext()"
    [channelLabel]="channelLabel()"
    [switching]="sectionSaving()"
    [loading]="loading || languagesLoading"
    [previewOpen]="showPreview()"
    [readinessLabel]="readiness.summary()?.label ?? null"
    [readinessTone]="readiness.summary()?.tone ?? 'block'"
    [canPublish]="(readiness.summary()?.canPublish ?? false) && hasPublishablePrice() && !isPackageClosed() && !scopeMoved()"
    [mayPublish]="mayPublish()"
    [hasPublishablePrice]="hasPublishablePrice()"
    [packageClosed]="isPackageClosed()"
    [canReject]="canReject()"
    [readinessPercent]="readiness.percent().percent"
    [readinessComplete]="readiness.percent().complete"
    [readinessTotal]="readiness.percent().total"
    [saveState]="saveState()"
    [lastSavedAt]="lastSavedAt()"
    [relativeNow]="relativeNow()"
    [activeLanguage]="languageMode.active()"
    [activeLanguages]="languageConfig.active()"
    [coverage]="readiness.languageCoverage()"
    (languageSelect)="setLanguageMode($event)"
    (priceSelect)="onPriceSelect($event, true)"
    (openPackageSettings)="onTabChange('package-settings')"
    (cancelClick)="onCancel()"
    (publishClick)="onPublish()"
    (rejectClick)="onReject()"
    (togglePreview)="togglePreview()"
    (fieldJump)="onCoverageFieldJump($event)"
  ></app-enrichment-edit-package-hero>

  <!-- Workspace: editor (left) + sticky live preview (right, desktop) -->
  <div
    class="enrichment-workspace"
    [class.enrichment-workspace--preview-open]="showPreview()"
  >
    <div class="enrichment-workspace__editor">
      @if (!loading && !languagesLoading) { @switch (pane()) { @case ('refusal')
      {
      <!-- The link names a price this package does not carry: refuse, never
           remap, because every write would land on the wrong price. -->
      <div
        class="m-6 enr-bg-surface rounded-lg p-10 shadow-sm flex flex-col items-center text-center gap-4"
        role="alert"
        data-testid="enrichment-price-not-on-package"
      >
        <div class="enr-typo-title">
          {{ refusedPriceId() }} is not a price of this package
        </div>
        <div class="enr-text-surface text-sm max-w-md">
          The link names a price that {{ packageDisplayName }} does not carry on
          this channel. Open its first price, or go back to the list.
        </div>
        <div class="flex gap-3">
          <enr-button
            variant="primary"
            size="medium"
            (clicked)="openFirstPrice()"
            data-testid="enrichment-open-first-price"
          >
            Open the first price
          </enr-button>
          <enr-button
            variant="outline"
            size="medium"
            (clicked)="returnToList()"
          >
            Back to the list
          </enr-button>
        </div>
      </div>
      } @case ('editor') {
      <div class="flex flex-col space-y-6 m-6">
        @if (active.state().kind === 'no-prices') {
        <div
          class="enr-bg-surface rounded-lg p-4 shadow-sm text-sm enr-text-surface"
          role="status"
          data-testid="enrichment-no-prices"
        >
          This package has no prices to edit on this channel. Its package
          settings can still be changed.
        </div>
        }
        <!-- Workflow stepper — numbered customer-facing steps.
             Step indicator color reflects readiness (error/warning/in-progress). -->
        <app-enrichment-edit-package-stepper
          [steps]="visibleSteps()"
          [activeId]="activeTab()"
          (stepSelect)="onTabChange($event)"
        ></app-enrichment-edit-package-stepper>

        <!-- Readiness banner — persistent per-tab issue list above the editor -->
        <app-tab-readiness-banner
          [summary]="activeTabReadiness()"
          [tabLabel]="activeTabLabel()"
          (issueSelect)="onBannerIssueSelect($event)"
        ></app-tab-readiness-banner>

        <!-- Editor pane. A price tab is re-created when the price changes
             (the @for key), like a tab switch; see `paneKey`. While the tab is dirty
             the fixed save-bar dock floats over the pane's last row, so the
             pane grows by the dock's height to keep that row reachable. -->
        <div class="enrichment-editor-pane" [class.pb-20]="sectionBarDirty()">
          @for (key of [paneKey()]; track key) { @switch (activeTab()) { @case
          ('price-config') {
          <app-enrichment-pricing [focus]="paneFocus()" />
          } @case ('category') {
          <app-enrichment-shelf />
          } @case ('package-settings') {
          <app-enrichment-package-settings />
          } @case ('special-promotion') {
          <app-enrichment-special-promotion />
          } @default {
          <div
            class="enr-bg-surface rounded-lg p-8 shadow-sm flex flex-col items-center text-center gap-3"
            role="status"
          >
            <div class="enr-typo-title">Content section not found</div>
            <div class="enr-text-surface text-sm max-w-md">
              We couldn't find this section. Pick one from the workflow above to
              continue.
            </div>
          </div>
          } } }
        </div>

        <!-- Contextual save bar — renders nothing while the active tab is
             clean; slides up from the bottom edge on the first edit, naming
             the tab it saves. -->
        <app-section-save-bar
          [dirty]="sectionBarDirty()"
          [saving]="sectionSaving()"
          [tabLabel]="activeTabShortLabel()"
          [stageStatus]="sectionBarStageStatus()"
          [lockReason]="saveRefusalMessage()"
          (save)="onSectionSave()"
          (discard)="onSectionDiscard()"
          (stage)="onStagePrice()"
        ></app-section-save-bar>
      </div>
      } @default {
      <ng-container *ngTemplateOutlet="loadingSpinner"></ng-container>
      } } } @else {
      <ng-container *ngTemplateOutlet="loadingSpinner"></ng-container>
      }
    </div>

    <!-- Scrim for the narrow-mode overlay drawer. Present in the DOM only when
         the preview is open; CSS decides whether the drawer layout is active at
         all, so on desktop this element is `display: none`. -->
    @if (showPreview()) {
    <button
      type="button"
      class="enrichment-preview-scrim"
      aria-label="Hide preview"
      (click)="togglePreview()"
    ></button>
    }

    <div
      class="enrichment-workspace__preview"
      [attr.inert]="showPreview() ? null : ''"
    >
      @if (pane() === 'editor') {
      <app-enrichment-preview-panel
        [popId]="active.popId()"
        (collapse)="togglePreview()"
      ></app-enrichment-preview-panel>
      }
    </div>
  </div>

  <ng-template #loadingSpinner>
    <div
      class="m-6 enr-bg-surface rounded-lg p-10 shadow-sm flex flex-col items-center text-center gap-4"
    >
      <div
        class="enrichment-shell__loading-spinner"
        role="status"
        aria-live="polite"
        aria-label="Loading enrichment package"
      ></div>
      <div class="enr-typo-title">Preparing your package…</div>
      <div class="enr-text-surface text-sm max-w-md">
        Please wait while we gather the latest details for you.
      </div>
    </div>
  </ng-template>

  <!-- Keyboard shortcut help overlay (triggered by `?`) -->
  @if (shortcutHelpOpen()) {
  <div
    class="shortcut-help-overlay"
    role="presentation"
    (click)="closeShortcutHelp()"
  >
    <div
      class="shortcut-help"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shortcut-help-title"
      tabindex="-1"
      #shortcutHelpDialog
      (click)="$event.stopPropagation()"
      (keydown.enter)="$event.stopPropagation()"
    >
      <div class="shortcut-help__head">
        <h2 id="shortcut-help-title" class="shortcut-help__title">
          Keyboard shortcuts
        </h2>
        <button
          type="button"
          class="shortcut-help__close"
          (click)="closeShortcutHelp()"
          aria-label="Close shortcut help"
        >
          ×
        </button>
      </div>
      <dl class="shortcut-help__list">
        <div class="shortcut-help__row">
          <dt>
            <kbd>⌘</kbd><span aria-hidden="true">/</span><kbd>Ctrl</kbd> +
            <kbd>S</kbd>
          </dt>
          <dd>Save draft</dd>
        </div>
        <div class="shortcut-help__row">
          <dt>
            <kbd>⌘</kbd><span aria-hidden="true">/</span><kbd>Ctrl</kbd> +
            <kbd>Shift</kbd> + <kbd>P</kbd>
          </dt>
          <dd>Review &amp; publish</dd>
        </div>
        <div class="shortcut-help__row">
          <dt><kbd>←</kbd> <kbd>→</kbd></dt>
          <dd>Previous / next step</dd>
        </div>
        <div class="shortcut-help__row">
          <dt><kbd>Esc</kbd></dt>
          <dd>Close menus &amp; this help</dd>
        </div>
        <div class="shortcut-help__row">
          <dt><kbd>?</kbd></dt>
          <dd>Toggle this help</dd>
        </div>
      </dl>
      <p class="shortcut-help__foot">
        Shortcuts are skipped while typing in form fields.
      </p>
      <div class="shortcut-help__tour-link">
        <button
          type="button"
          class="shortcut-help__tour-btn"
          (click)="restartTour()"
        >
          ▶ Replay onboarding tour
        </button>
      </div>
    </div>
  </div>
  }

  <!-- Onboarding tour (first visit + replay) -->
  <app-enrichment-edit-package-tour
    [open]="tourOpen()"
    (done)="onTourDone()"
    (skipped)="onTourDone()"
  ></app-enrichment-edit-package-tour>

  <ng-template #publishReviewTemplate>
    @if (drawerTarget(); as target) {
    <div class="publish-review os-overlay">
      <div
        class="publish-review__section"
        data-testid="enrichment-publish-drawer-scope"
      >
        <div class="publish-review__section-title">
          {{ drawerMode() === "reject" ? "Price to reject" : "Price to publish"
          }}
        </div>
        <div
          class="publish-review__price-row"
          [class.publish-review__price-row--disabled]="!target.eligibility.ok"
        >
          <app-price-offset-badge [offset]="target.entry.offset" />
          <span class="publish-review__price-label">
            <span
              class="publish-review__price-name"
              [title]="target.entry.title"
              >{{ target.entry.title }}</span
            >
            <code class="publish-review__price-id" [title]="target.entry.id"
              >{{ target.entry.id }}</code
            >
          </span>
          @if (target.entry.status; as status) {
          <enr-status
            size="small"
            [status]="status.type"
            [customText]="status.label"
          />
          } @if (!target.eligibility.ok) {
          <span
            class="publish-review__price-reason"
            data-testid="enrichment-publish-drawer-price-reason"
            >{{ target.eligibility.reason }}</span
          >
          }
        </div>
        <div class="enr-text-surface text-sm">
          A price of {{ packageDisplayName }}. Its siblings are not affected.
        </div>
      </div>

      <!-- Readiness audit — gates PUTTING a price on sale, so the reject mode
           (taking it off) hides it entirely. This price's issues plus the
           package-level ones; never a sibling's. -->
      @if (drawerMode() !== "reject") {
      <div class="publish-review__section">
        <div class="publish-review__section-title">Content Readiness</div>
        @for (section of readiness.publish().sections; track section.id) {
        <div
          class="publish-review__item"
          [class.publish-review__item--complete]="section.status === 'complete'"
          [class.publish-review__item--warning]="section.status === 'warning'"
          [class.publish-review__item--error]="section.status === 'error'"
          [class.publish-review__item--empty]="section.status === 'empty'"
        >
          <span class="publish-review__item-icon" aria-hidden="true">
            @switch (section.status) { @case ('complete') {
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            } @case ('warning') {
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 9v4M12 17h.01" />
              <path
                d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
              />
            </svg>
            } @case ('error') {
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            } @case ('empty') {
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="9" />
            </svg>
            } }
          </span>
          <div class="publish-review__item-body">
            <div class="publish-review__item-label">
              {{ getTabDisplayLabel(section.id, section.label) }}
            </div>
            @for (issue of section.issues; track issue.message) {
            <button
              type="button"
              class="publish-review__item-issue publish-review__item-issue--action"
              [class.publish-review__item-issue--error]="issue.severity === 'error'"
              [class.publish-review__item-issue--warning]="issue.severity === 'warning'"
              (click)="navigateFromPublishIssue(issue, section.id)"
            >
              <span class="publish-review__item-issue-text">
                {{ issue.message }}
              </span>
              <span class="publish-review__item-issue-action">
                {{ publishIssuePrefix(issue) }} in {{
                getTabDisplayLabel(issue.tabId || section.id, section.label) }}
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </span>
            </button>
            } @empty {
            <div
              class="publish-review__item-issue publish-review__item-issue--complete"
            >
              Ready for customers.
            </div>
            }
          </div>
        </div>
        }
      </div>

      @if (readiness.publish().errors.length > 0) {
      <div class="publish-review__summary publish-review__summary--error">
        <span class="publish-review__summary-icon" aria-hidden="true">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </span>
        <span>
          <strong
            >Please fix {{ readiness.publish().errors.length }} {{
            readiness.publish().errors.length === 1 ? "issue" : "issues"
            }}</strong
          >
          before this price can be published.
        </span>
      </div>
      } @if (readiness.publish().errors.length === 0 &&
      readiness.publish().warnings.length > 0) {
      <div class="publish-review__summary publish-review__summary--warning">
        <span class="publish-review__summary-icon" aria-hidden="true">
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 9v4M12 17h.01" />
            <path
              d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            />
          </svg>
        </span>
        <span>
          <strong
            >{{ readiness.publish().warnings.length }} {{
            readiness.publish().warnings.length === 1 ? "warning" : "warnings"
            }}</strong
          >
          to review — you can still publish, but they may affect the customer
          experience.
        </span>
      </div>
      }
      <!-- /readiness (publish mode) -->
      }

      <!-- Now, or queued for a chosen time. A price already armed in this
           direction can be acted on now but not queued twice (the API
           answers 409). Hidden entirely with enablePublishSchedule off. -->
      @if (drawerScheduleEnabled()) {
      <div
        class="publish-review__section publish-review__timing"
        role="radiogroup"
        [attr.aria-label]="drawerMode() === 'reject' ? 'When to take this price off sale' : 'When to publish this price'"
        data-testid="enrichment-publish-drawer-timing"
      >
        <enr-radio
          id="enrichment-publish-drawer-timing-now"
          name="publishDrawerTiming"
          value="now"
          [label]="drawerMode() === 'reject' ? 'Take off sale now' : 'Publish now'"
          [checked]="drawerTiming() === 'now'"
          (change)="onDrawerTimingChange($event)"
          [attr.data-testid]="'enrichment-publish-drawer-timing-now'"
        />
        <enr-radio
          id="enrichment-publish-drawer-timing-later"
          name="publishDrawerTiming"
          value="later"
          [label]="drawerMode() === 'reject' ? 'Schedule the take-down' : 'Schedule the publish'"
          [checked]="drawerTiming() === 'later'"
          (change)="onDrawerTimingChange($event)"
          [attr.data-testid]="'enrichment-publish-drawer-timing-later'"
        />
        @if (drawerTiming() === "later") {
        <div class="publish-review__timing-picker">
          <enr-date-time-picker
            [label]="drawerMode() === 'reject' ? 'take-down time' : 'publish time'"
            [value]="drawerScheduledAtLocal()"
            (valueChange)="setDrawerScheduledAt($event)"
            data-testid="enrichment-publish-drawer-datetime"
          />
          <p
            class="publish-review__timing-zone"
            data-testid="enrichment-publish-drawer-timing-zone"
          >
            {{ drawerScheduleZoneLabel }}
          </p>
          @if (drawerScheduledAtIsPast()) {
          <p
            class="publish-review__timing-error"
            role="alert"
            data-testid="enrichment-publish-drawer-timing-past"
          >
            That time has passed — pick a time in the future.
          </p>
          } @if (drawerOpenScheduleHint(); as hint) {
          <p
            class="publish-review__timing-error"
            role="alert"
            data-testid="enrichment-publish-drawer-already-scheduled"
          >
            {{ hint }}. Cancel that schedule from the package list before
            setting a new one.
          </p>
          }
        </div>
        }
      </div>
      } @if (drawerMode() === "reject") {
      <div class="publish-review__summary publish-review__summary--warning">
        @if (drawerTiming() === "later") {
        <!-- A scheduled take-down is NOT a deferred reject: the queued job
             returns the price to Designed, where a reject leaves it Rejected.
             Saying "Rejected" here would promise the wrong end state. -->
        <span data-testid="enrichment-publish-drawer-takedown-note">
          Nothing changes now. At the time you pick, this price comes off sale
          on this channel and returns to Designed — its enrichment is kept.
          Other channels are not affected. Cancel a queued take-down from the
          package list while it is still pending.
        </span>
        } @else {
        <span>
          This price is withdrawn from this channel — its status becomes
          Rejected. Saved enrichment is kept, so you can publish it again later.
          Other channels are not affected. @if (isDirty()) { Your unsaved
          changes on this page are not part of this and will be discarded when
          you return to the package list. }
        </span>
        }
      </div>
      }

      <div class="publish-review__footer">
        <enr-button
          variant="outline"
          size="medium"
          (clicked)="closePublishReview()"
        >
          Back to editor
        </enr-button>
        <enr-button
          [variant]="drawerMode() === 'reject' && drawerTiming() !== 'later' ? 'error' : 'primary'"
          size="medium"
          [disabled]="!drawerCanProceed()"
          (clicked)="confirmDrawerAction()"
          [attr.data-testid]="'enrichment-publish-drawer-btn-proceed'"
        >
          @if (drawerMode() === "reject") { {{ drawerTiming() === "later" ?
          "Schedule take-down" : "Proceed to reject" }} } @else { {{
          drawerTiming() === "later" ? "Schedule publish" : "Proceed to publish"
          }} }
        </enr-button>
      </div>
    </div>
    }
  </ng-template>

  <!-- Pre-save confirm body when an on-sale price may stay on sale: one choice
       for the whole save. Title/description and the Save/Cancel footer come
       from the modal config (see confirmPriceSave). -->
  <ng-template #priceSaveConfirmTemplate>
    @if (priceSaveConfirmCopy(); as copy) { @if (copy.mode === "choose") {
    <div class="price-save-confirm os-overlay" data-testid="enrichment-price-save-confirm">
      <div class="price-save-confirm__option">
        <enr-radio
          id="enrichment-price-save-confirm-keep"
          name="price-save-lifecycle"
          value="keep"
          [label]="copy.options.keep.label"
          [ngModel]="priceSaveLiveChoice()"
          (ngModelChange)="priceSaveLiveChoice.set($event)"
          [attr.data-testid]="'enrichment-price-save-confirm-keep'"
        />
        <p class="price-save-confirm__hint">{{ copy.options.keep.hint }}</p>
      </div>
      <div class="price-save-confirm__option">
        <enr-radio
          id="enrichment-price-save-confirm-off-sale"
          name="price-save-lifecycle"
          value="offSale"
          [label]="copy.options.offSale.label"
          [ngModel]="priceSaveLiveChoice()"
          (ngModelChange)="priceSaveLiveChoice.set($event)"
          [attr.data-testid]="'enrichment-price-save-confirm-off-sale'"
        />
        <p class="price-save-confirm__hint">{{ copy.options.offSale.hint }}</p>
      </div>
    </div>
    } }
  </ng-template>
</div>
